import { useCreateTelantAdmin } from "api/tenant/service";
import React, { useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import { formatPhoneNumber, validatePhoneNumber } from "../../../utils/phoneUtils";
import { useGetCountries } from "api/countries/service";
import { useTranslation } from "react-i18next";
import Select from "components/Select";
import { useNotification } from "../../../contexts/NotificationContext";
import { useParams } from "react-router-dom";
import { getChannelTypeOptions } from "../../../constants/channelTypes";

const BookkeepingTab = ({
  activeColor = "blue",
  activeColorStyle,
  tenantData,
  onNext,
  onPrev,
  showNext,
  selectStyles,
  inputStyle,
  inputClassName,
}) => {
  const { id: code } = useParams();
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const createTenantAdminMutation = useCreateTelantAdmin();
  const { data: countriesData } = useGetCountries();
  const countryList = countriesData?.data || [];

  const countryOptions = [{ value: '', label: t('channelsEmbedded.selectCountry') }, ...countryList.map(c => ({ value: String(c.id), label: c.name }))];

  const typeOptions = useMemo(() => getChannelTypeOptions(t), [t]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'bookkeeping_company', // Defaulting to Bookkeeping Company
    status: 'active',
    fed_id: '',
    legal_name: '',
    address_1: '',
    address_2: '',
    zip: '',
    city: '',
    state: '',
    county: '',
    country: '',
    contact_person: '',
    phone_1: '',
    phone_2: '',
    fax_1: '',
    fax_2: '',
    website: '',
    email: '',
    text_name: '',
    accounting_class: '',
  });

  React.useEffect(() => {
    if (tenantData?.bookkeeping_company) {
      const bc = tenantData.bookkeeping_company;
      setFormData(prev => ({
        ...prev,
        name: bc.name || '',
        description: bc.description || '',
        type: bc.type || 'bookkeeping_company',
        status: bc.channel_status || 'active',
        fed_id: bc.fed_id || '',
        legal_name: bc.legal_name || '',
        address_1: bc.address_1 || '',
        address_2: bc.address_2 || '',
        zip: bc.zip || '',
        city: bc.city || '',
        state: bc.state ? String(bc.state.id || bc.state) : '',
        country: bc.country ? String(bc.country.id || bc.country) : '',
        county: bc.county || '',
        contact_person: bc.contact_person || '',
        phone_1: bc.phone_1 || '',
        phone_2: bc.phone_2 || '',
        fax_1: bc.fax_1 || '',
        fax_2: bc.fax_2 || '',
        website: bc.website || '',
        email: bc.email || '',
        text_name: bc.text_name || '',
        accounting_class: bc.accounting_class || '',
      }));
    }
  }, [tenantData]);
  const [errors, setErrors] = useState({});

  // Country → State: compute after formData exists
  const selectedCountry = useMemo(() => countryList.find(c => String(c.id) === String(formData.country)), [countryList, formData.country]);
  const stateOptions = selectedCountry?.states || [];
  const hasStates = Array.isArray(stateOptions) && stateOptions.length > 0;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let formattedValue = value;
    if (name.includes('phone') || name.includes('fax')) {
      formattedValue = formatPhoneNumber(value);
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : formattedValue,
      ...(name === 'country' ? { state: '' } : {}),
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const missingFields = [];

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'Name is required';
      missingFields.push(t("channelsAdd.descriptiveName"));
    }
    if (!formData.legal_name || !formData.legal_name.trim()) {
      newErrors.legal_name = 'Legal name is required';
      missingFields.push(t("channelsAdd.legalName"));
    }
    if (!formData.address_1 || !formData.address_1.trim()) {
      newErrors.address_1 = 'Address 1 is required';
      missingFields.push(t("channelsAdd.address1"));
    }
    if (!formData.zip || !formData.zip.trim()) {
      newErrors.zip = 'ZIP is required';
      missingFields.push(t("channelsAdd.zip"));
    }
    if (!formData.city) {
      newErrors.city = 'City is required';
      missingFields.push(t("channelsAdd.city"));
    }
    const requireState = hasStates;
    if (requireState && !formData.state) {
      newErrors.state = 'State is required';
      missingFields.push(t("channelsAdd.state"));
    }
    if (!formData.country) {
      newErrors.country = 'Country is required';
      missingFields.push(t("channelsAdd.country"));
    }
    if (!formData.phone_1 || !formData.phone_1.trim()) {
      newErrors.phone_1 = 'Phone 1 is required';
      missingFields.push(t("channelsAdd.phone1"));
    } else {
      const phoneError = validatePhoneNumber(formData.phone_1);
      if (phoneError) {
        newErrors.phone_1 = phoneError;
        missingFields.push(t("channelsAdd.phone1"));
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const fieldsList = missingFields.join(', ');
      showNotification(
        'danger',
        `${t('channelsAdd.requiredFieldsMissing')}: ${fieldsList}`
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const payload = {
        code: code,
        bookkeeping_company: {
          name: formData.name,
          type: formData.type,
          description: formData.description,
          channel_status: formData.status,
          fed_id: formData.fed_id,
          legal_name: formData.legal_name,
          address_1: formData.address_1,
          address_2: formData.address_2,
          zip: formData.zip,
          city: formData.city === '' ? null : formData.city,
          county: formData.county,
          country: formData.country,
          state: formData.state,
          contact_person: formData.contact_person,
          phone_1: formData.phone_1,
          phone_2: formData.phone_2,
          fax_1: formData.fax_1,
          fax_2: formData.fax_2,
          website: formData.website,
          email: formData.email,
          text_name: formData.text_name,
          accounting_class: formData.accounting_class,
        }
      };

      await createTenantAdminMutation.mutateAsync(payload);
      showNotification('success', 'Bookkeeping Company created successfully!');
      if (onNext) onNext();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Card className="shadow-none border-0 mb-0">
      <CardBody className="px-0 py-0">
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="type" className="form-control-label">{t('channelsEmbedded.type')}</Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder={t('channelsEmbedded.selectType')}
                  name="type"
                  isSearchable={false}
                  closeMenuOnSelect={true}
                  value={formData.type ? {
                    value: formData.type,
                    label: typeOptions.find(opt => opt.value === formData.type)?.label || formData.type
                  } : null}
                  onChange={(option) => {
                    handleInputChange({ target: { name: 'type', value: option?.value || '' } });
                  }}
                  options={typeOptions}
                  isDisabled={true}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="name" className="form-control-label">{t('channelsEmbedded.descriptiveName')} <span style={{ color: 'red' }}>*</span></Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? 'is-invalid' : ''}
                  placeholder={t('channelsEmbedded.enterChannelName')}
                  disabled={createTenantAdminMutation.isPending}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="description" className="form-control-label">{t('channelsEmbedded.description')}</Label>
                <Input
                  id="description"
                  name="description"
                  type="text"
                  value={formData.description}
                  onChange={handleInputChange}
                  className={errors.description ? 'is-invalid' : ''}
                  placeholder={t('channelsEmbedded.enterDescription')}
                  disabled={createTenantAdminMutation.isPending}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="fed_id" className="form-control-label">{t('channelsEmbedded.fedId')}</Label>
                <Input
                  id="fed_id"
                  name="fed_id"
                  type="text"
                  value={formData.fed_id}
                  onChange={handleInputChange}
                  placeholder={t('channelsEmbedded.enterFedId')}
                  disabled={createTenantAdminMutation.isPending}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="legal_name" className="form-control-label">{t('channelsEmbedded.legalName')} <span style={{ color: 'red' }}>*</span></Label>
                <Input
                  id="legal_name"
                  name="legal_name"
                  type="text"
                  value={formData.legal_name}
                  onChange={handleInputChange}
                  className={errors.legal_name ? 'is-invalid' : ''}
                  placeholder={t('channelsEmbedded.enterLegalName')}
                  disabled={createTenantAdminMutation.isPending}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="address_1" className="form-control-label">{t('channelsEmbedded.address1')} <span style={{ color: 'red' }}>*</span></Label>
                <Input
                  id="address_1"
                  name="address_1"
                  type="text"
                  value={formData.address_1}
                  onChange={handleInputChange}
                  className={errors.address_1 ? 'is-invalid' : ''}
                  placeholder={t('channelsEmbedded.enterAddress1')}
                  disabled={createTenantAdminMutation.isPending}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="address_2" className="form-control-label">{t('channelsEmbedded.address2')}</Label>
                <Input
                  id="address_2"
                  name="address_2"
                  type="text"
                  value={formData.address_2}
                  onChange={handleInputChange}
                  placeholder={t('channelsEmbedded.enterAddress2')}
                  disabled={createTenantAdminMutation.isPending}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="zip" className="form-control-label">{t('channelsEmbedded.zip')} <span style={{ color: 'red' }}>*</span></Label>
                <Input
                  id="zip"
                  name="zip"
                  type="text"
                  value={formData.zip}
                  onChange={handleInputChange}
                  className={errors.zip ? 'is-invalid' : ''}
                  placeholder={t('channelsEmbedded.enterZip')}
                  disabled={createTenantAdminMutation.isPending}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="city" className="form-control-label">{t('channelsEmbedded.city')} <span style={{ color: 'red' }}>*</span></Label>
                <Input
                  id="city"
                  name="city"
                  type="text"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={errors.city ? 'is-invalid' : ''}
                  disabled={createTenantAdminMutation.isPending}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="country" className="form-control-label">{t('channelsEmbedded.country')} <span style={{ color: 'red' }}>*</span></Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder={t('channelsEmbedded.selectCountry')}
                  name="country"
                  isSearchable
                  closeMenuOnSelect={true}
                  value={formData.country ? {
                    value: formData.country,
                    label: countryOptions.find(opt => opt.value === formData.country)?.label || formData.country
                  } : null}
                  onChange={(option) => {
                    handleInputChange({ target: { name: 'country', value: option?.value || '' } });
                  }}
                  options={countryOptions}
                  isDisabled={createTenantAdminMutation.isPending}
                  isClearable
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="state" className="form-control-label">{t('channelsEmbedded.state')} <span style={{ color: 'red' }}>*</span></Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder={
                    !formData.country ? t('channelsEmbedded.selectCountryFirst') :
                      formData.country && !hasStates ? t('channelsEmbedded.noStates') :
                        t('channelsEmbedded.selectState')
                  }
                  name="state"
                  isSearchable={false}
                  closeMenuOnSelect={true}
                  value={formData.state ? {
                    value: formData.state,
                    label: stateOptions.find(opt => String(opt.id) === formData.state)?.name || formData.state
                  } : null}
                  onChange={(option) => {
                    handleInputChange({ target: { name: 'state', value: option?.value || '' } });
                  }}
                  options={hasStates ? stateOptions.map(option => ({
                    value: String(option.id),
                    label: option.name
                  })) : []}
                  isDisabled={createTenantAdminMutation.isPending || !hasStates}
                  isClearable
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="county" className="form-control-label">{t('channelsEmbedded.county')}</Label>
                <Input
                  id="county"
                  name="county"
                  type="text"
                  value={formData.county}
                  onChange={handleInputChange}
                  placeholder={t('channelsEmbedded.enterCounty')}
                  disabled={createTenantAdminMutation.isPending}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="contact_person" className="form-control-label">{t('channelsEmbedded.contactPerson')}</Label>
                <Input
                  id="contact_person"
                  name="contact_person"
                  type="text"
                  value={formData.contact_person}
                  onChange={handleInputChange}
                  placeholder={t('channelsEmbedded.enterContactPerson')}
                  disabled={createTenantAdminMutation.isPending}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="phone_1" className="form-control-label">{t('channelsEmbedded.phone1')} <span style={{ color: 'red' }}>*</span></Label>
                <Input
                  id="phone_1"
                  name="phone_1"
                  type="text"
                  value={formData.phone_1}
                  onChange={handleInputChange}
                  className={errors.phone_1 ? 'is-invalid' : ''}
                  placeholder={t('channelsEmbedded.enterPhoneNumber')}
                  disabled={createTenantAdminMutation.isPending}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="email" className="form-control-label">{t('channelsEmbedded.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('channelsEmbedded.enterEmail')}
                  disabled={createTenantAdminMutation.isPending}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="website" className="form-control-label">{t('channelsEmbedded.website')}</Label>
                <Input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder={t('channelsEmbedded.enterWebsite')}
                  disabled={createTenantAdminMutation.isPending}
                />
              </FormGroup>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <div className="mr-auto">
              <Button
                color="secondary"
                outline
                onClick={onPrev}
                disabled={createTenantAdminMutation.isPending}
              >
                Previous Step
              </Button>
            </div>
            <Button
              style={{ ...activeColorStyle, color: 'white' }}
              type="submit"
              disabled={createTenantAdminMutation.isPending}
              className="px-4"
            >
              {createTenantAdminMutation.isPending ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  Save & Continue
                </>
              )}
            </Button>
            {showNext && (
              <Button
                color="secondary"
                onClick={onNext}
                disabled={createTenantAdminMutation.isPending}
              >
                Next Step
              </Button>
            )}
          </div>
        </Form>
      </CardBody>
    </Card>
  );
};

export default BookkeepingTab;
